/**
 * session-expired-modal.tsx — Hộp "Phiên đăng nhập đã hết hạn" hiện ngay trên trang đang làm việc (gắn trong AppLayout).
 *
 * Mở khi refresh token bị từ chối / token hỏng (api-client `forceLogout` → session-expiry `requestRelogin`). Trang phía
 * sau giữ nguyên (mờ đi), request API bị giữ lại. Đăng nhập lại:
 *  - cùng người + cùng tổ chức → thả các request đang giữ, làm mới dữ liệu, làm tiếp;
 *  - tài khoản / tổ chức khác → tải lại trang để không trộn dữ liệu.
 * "Đăng xuất" / "Đặt lại mật khẩu" → rời hẳn phiên cũ.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { clearTokens, hardLogout } from '@/lib/api-client'
import { queryClient } from '@/lib/query-client'
import { disconnectSocket } from '@/lib/socket'
import { decideAfterRelogin, registerSessionHost, resumeSession, useSessionModal } from '@/lib/session-expiry'
import { useAuthStore } from '@/stores/auth-store'
import type { AuthUser } from '@/types/api'
import { AuthDialogFrame, AuthLayout } from '@/pages/auth/auth-layout'
import { LoginForm } from '@/pages/auth/login-form'

export function SessionExpiredModal() {
  const expired = useSessionModal((s) => s.status === 'expired')
  useEffect(() => registerSessionHost(), [])
  return expired ? <ReloginDialog /> : null
}

function ReloginDialog() {
  const { t } = useTranslation('sharedMisc')
  // Người dùng lúc hết phiên (store vẫn giữ để trang phía sau còn hiển thị).
  const [prev] = useState(() => useAuthStore.getState().user)

  useEffect(() => {
    // Token đã bị xoá — ngắt realtime; đăng nhập lại (auth-store) sẽ tự nối lại.
    disconnectSocket()
  }, [])

  const onLoggedIn = useCallback((user: AuthUser) => {
    if (decideAfterRelogin(prev, user) === 'reload') {
      window.location.reload()
      return
    }
    resumeSession()
    void queryClient.invalidateQueries()
    toast.success(t('sessionExpiredModal.reloggedIn'))
  }, [prev, t])

  return (
    <AuthDialogFrame>
      <AuthLayout
        title={t('sessionExpiredModal.title')}
        subtitle={t('sessionExpiredModal.subtitle')}
        footer={(
          <>
            {t('sessionExpiredModal.notYou')}{' '}
            <button type="button" className="font-medium text-primary hover:underline" onClick={() => hardLogout()}>
              {t('sessionExpiredModal.logout')}
            </button>
          </>
        )}
      >
        <LoginForm
          initialEmail={prev?.email ?? ''}
          onLoggedIn={onLoggedIn}
          forgotLink={(
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => {
                clearTokens()
                window.location.href = '/forgot-password'
              }}
            >
              {t('sessionExpiredModal.resetPassword')}
            </button>
          )}
        />
      </AuthLayout>
    </AuthDialogFrame>
  )
}
